import { useContext, useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm, Controller } from "react-hook-form";
import styled from "styled-components";
import theme from "../styles/theme";
import Button from "./Button";
import FlexBox, { FlexGrow } from "./FlexBox";
import Input, { InputTextArea } from "./Input";
import { StyledText } from "./Text";
import Icon from "./Icon";
import Status from "../types/enum/Status";
import { CancelButton, CustomFlexFixed } from "./CourseOverviewModify";
import { useScreenClass } from "react-grid-system";
import { useLocation, useNavigate } from "react-router-dom";
import { SaveButton } from "./PillarOverviewModify";
import { LocationInterface } from "./UtilsComponents";
import { UserContext } from "../context/UserContext";
import ConfirmChanges from "./ConfirmChanges";
type KnowledgeAreaModifyType = {
  showBackButton?: boolean;
  goBack: () => void;
  onSaveComplete: (data: any) => void;
  knowledgeData: {
    id: string;
    description: string;
    title: string;
  };
};

const Header = styled.h4`
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: left;
`;
const CustomFlex = styled(FlexBox)`
  padding: 20px;
  align-items: center;
  border-bottom: 1px solid #3c406c;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 20px 0;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 150px;
  flex-grow: 0;
`;
const Label = styled.h5`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  color: black;
`;
interface SubdomainInputs {
  title: string;
  description: string;
}

function KnowledgeAreaModify({
  knowledgeData,
  goBack,
  showBackButton = false,
  onSaveComplete,
}: KnowledgeAreaModifyType) {
  const screenClass = useScreenClass();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { isDirty, errors },
  } = useForm<SubdomainInputs>({
    mode: "onBlur",
    defaultValues: {
      title: knowledgeData ? knowledgeData.title : "",
      description: knowledgeData ? knowledgeData.description : "",
    },
  });
  const { isModified, setModified, isOpen, setOpen } = useContext(UserContext);
  const [count, setCount] = useState({ name: 0, description: 0 });

  useEffect(() => {
    isDirty && setModified(true);
  }, [isDirty]);

  useEffect(() => {
    reset();
    mapData(knowledgeData);
  }, [knowledgeData]);

  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const onSubmit: SubmitHandler<SubdomainInputs> = (data: any) => {
    setOpen(false);
    setModified(false);
    mapData(knowledgeData);
    reset();
    onSaveComplete(data);
  };

  const mapData = (data: any) => {
    setValue("title", data.title);
    setValue("description", data.description);
  };

  const discardFunction = () => {
    setOpen(false);
    setModified(false);
    reset();
    // mapData(pillarData);
  };

  let location = useLocation() as LocationInterface;
  function navigateToList() {
    let strLocation = location.pathname.split("/");
    let removeEnd = strLocation.pop();
    let urlJoin = strLocation.join("/");
    return navigate(urlJoin);
  }
  return (
    <>
      {isOpen && (
        <ConfirmChanges
          discardFunction={discardFunction}
          isOpen={isOpen}
          setOpen={setOpen}
          confirmFunction={handleSubmit(onSubmit)}
          title={"Do you want to save your changes?"}
          description={
            "If you leave page, any changes you have made will be lost."
          }
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <CustomFlex
          style={{
            borderBottom: "none",
            alignItems: "flex-start",
            overflowY: "scroll",
          }}
        >
          <FlexGrow>
            <CustomFlexPlain>
              <CustomGrowPlain>
                <Label>Title</Label>
              </CustomGrowPlain>
              <FlexGrow>
                <Input
                  style={{ width: "100%" }}
                  isFullWidth
                  {...register("title", { required: true })}
                />
                {errors.title && (
                  <p
                    style={{
                      marginTop: 0,
                      color: theme.color.red800,
                    }}
                  >
                    This field is required
                  </p>
                )}
              </FlexGrow>
            </CustomFlexPlain>
            <CustomFlexPlain>
              <CustomGrowPlain>
                <Label>Description</Label>
              </CustomGrowPlain>
              <FlexGrow>
                <FlexBox
                  space={0}
                  alignItems="start"
                  style={{ width: "100%" }}
                  direction="column"
                >
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <>
                        <InputTextArea
                          isFullWidth
                          style={{ minHeight: "100px", width: "100%" }}
                          value={field.value}
                          maxLength={2048}
                          onChange={(e: any) => {
                            field.onChange(e);
                            setCount({
                              ...count,
                              description: e.target.value.length,
                            });
                          }}
                        />
                        <FlexGrow style={{ textAlign: "end" }}>
                          <StyledText
                            style={{ fontSize: "14px" }}
                            color={theme.color.lightBlueGray500}
                          >
                            {count.description || field.value.length}/2048
                          </StyledText>
                        </FlexGrow>
                      </>
                    )}
                  />
                  {/* <FlexGrow style={{ width: "100%" }}>
                    <InputTextArea
                      isFullWidth
                      style={{ minHeight: "100px", width: "100%" }}
                      // onChange={e => setCount(e.target.value.length)}
                      {...register("description")}
                    />
                  </FlexGrow>
                  <FlexGrow style={{ textAlign: "end" }}>
                    <StyledText
                      style={{ fontSize: "14px" }}
                      color={theme.color.lightBlueGray500}
                    >
                      0/1000
                    </StyledText>
                  </FlexGrow> */}
                </FlexBox>
              </FlexGrow>
            </CustomFlexPlain>
          </FlexGrow>
        </CustomFlex>
        <CustomFlexFixed>
          {showBackButton && (
            <Button
              key={0}
              style={{ marginLeft: "30px" }}
              type="button"
              onClick={() => {
                goBack();
              }}
              status={Status.primary}
              isOutline
            >
              <Icon style={{ paddingRight: "8px" }} name={"arrow_back"} /> Back
            </Button>
          )}
          <FlexGrow style={{ textAlign: "end", marginRight: "30px", padding: "10px 0px" }}>
            <CancelButton
              className="reduce_opct_onhover"
              key={1}
              type="button"
              onClick={() => {
                navigateToList();
              }}
              status={Status.danger}
              isOutline={false}
            >
              Cancel
            </CancelButton>
            <SaveButton
              className="reduce_opct_onhover"
              key={2}
              type="submit"
              status={Status.primary}
              isOutline={false}
            >
              <Icon style={{ paddingRight: "8px" }} name={"save"} /> Save
            </SaveButton>
          </FlexGrow>
        </CustomFlexFixed>
      </form>
    </>
  );
}
export default KnowledgeAreaModify;
